import { lazy } from "react";
import App from "./App.tsx";
import { createBrowserRouter } from "react-router-dom";
import Enrollment from "./pages/students/Enrollment.tsx";
import Login from "./pages/authentication/Login.tsx";
import Dashboard from "./pages/dashboard/Dashboard.tsx";
import PlanDetails from "./pages/plans/PlanDetails.tsx";
import PlansLayout from "./pages/plans/PlansLayout.tsx";
import PlansList from "./pages/plans/PlansList.tsx";
import StudentProfile from "./pages/students/StudentProfile.tsx";
import StudentsList from "./pages/students/StudentsList.tsx";
import PaymentFiles from "./pages/students/PaymentFiles.tsx";
import StudentsLayout from "./pages/students/StudentsLayout.tsx";
import NomenclaturesLayout from "./pages/nomenclatures/NomenclaturesLayout.tsx";
import EnrolledStudentsReport from "./pages/students/reports/EnrolledStudentsReport.tsx";
import TeachersLayout from "./pages/teachers/TeachersLayout.tsx";
import TeachersList from "./pages/teachers/TeachersList.tsx";
import TeacherProfile from "./pages/teachers/TeacherProfile.tsx";
import TeachersReport from "./pages/teachers/TeachersReport.tsx";
import ProtocolsLayout from "./pages/protocols/ProtocolsLayout.tsx";
import ProtocolsList from "./pages/protocols/ProtocolsList.tsx";
import ProtocolDetails from "./pages/protocols/ProtocolDetails.tsx";
import ProtocolCreate from "./pages/protocols/ProtocolCreate.tsx";
import ScheduleLayout from "./pages/schedule/ScheduleLayout.tsx";
import SchedulesList from "./pages/schedule/SchedulesList.tsx";
import ScheduleDetails from "./pages/schedule/ScheduleDetails.tsx";
import NotTakenExamsReport from "./pages/students/reports/NotTakenExamsReport.tsx";
import StudentCountsGroupedReport from "./pages/students/reports/StudentCountsGroupedReport.tsx";
import GroupAverageGradeReport from "./pages/students/reports/GroupAverageGradeReport.tsx";
import GroupGradesReport from "./pages/students/reports/GroupGradesReport.tsx";
import ProtocolDetailsRecognition from "./pages/protocols/ProtocolDetailsRecognition.tsx";
import Help from "./pages/help/Help.tsx";

const AcademicPositions = lazy(() => import("./pages/nomenclatures/AcademicPositions.tsx"));
const AcademicRanks = lazy(() => import("./pages/nomenclatures/AcademicRanks.tsx"));
const BankStatuses = lazy(() => import("./pages/nomenclatures/BankStatuses.tsx"));
const AdmissionReasons = lazy(() => import("./pages/nomenclatures/AdmissionReasons.tsx"));
const AcademicYears = lazy(() => import("./pages/nomenclatures/AcademicYears.tsx"));
const Cities = lazy(() => import("./pages/nomenclatures/Cities.tsx"));
const Buildings = lazy(() => import("./pages/nomenclatures/Buildings.tsx"));
const Countries = lazy(() => import("./pages/nomenclatures/Countries.tsx"));
const Degrees = lazy(() => import("./pages/nomenclatures/Degrees.tsx"));
const Departments = lazy(() => import("./pages/nomenclatures/Departments.tsx"));
const Faculties = lazy(() => import("./pages/nomenclatures/Faculties.tsx"));
const FnPositions = lazy(() => import("./pages/nomenclatures/FnPositions.tsx"));
const FeeTypes = lazy(() => import("./pages/nomenclatures/FeeTypes.tsx"));
const Groups = lazy(() => import("./pages/nomenclatures/Groups.tsx"));
const IdentificationTypes = lazy(() => import("./pages/nomenclatures/IdentificationTypes.tsx"));
const MasterDegreeTypes = lazy(() => import("./pages/nomenclatures/MasterDegreeTypes.tsx"));
const Municipalities = lazy(() => import("./pages/nomenclatures/Municipalities.tsx"));
const PlanTypes = lazy(() => import("./pages/nomenclatures/PlanTypes.tsx"));
const Qualifications = lazy(() => import("./pages/nomenclatures/Qualifications.tsx"));
const Regions = lazy(() => import("./pages/nomenclatures/Regions.tsx"));
const SchoolProfiles = lazy(() => import("./pages/nomenclatures/SchoolProfiles.tsx"));
const Schools = lazy(() => import("./pages/nomenclatures/Schools.tsx"));
const SemesterTypes = lazy(() => import("./pages/nomenclatures/SemesterTypes.tsx"));
const SessionTypes = lazy(() => import("./pages/nomenclatures/SessionTypes.tsx"));
const Specialities = lazy(() => import("./pages/nomenclatures/Specialities.tsx"));
const SpecialityFees = lazy(() => import("./pages/nomenclatures/SpecialityFees.tsx"));
const SpecialityForms = lazy(() => import("./pages/nomenclatures/SpecialityForms.tsx"));
const SpecialityModes = lazy(() => import("./pages/nomenclatures/SpecialityModes.tsx"));
const SpecialityTypes = lazy(() => import("./pages/nomenclatures/SpecialityTypes.tsx"));
const StudentStatuses = lazy(() => import("./pages/nomenclatures/StudentStatuses.tsx"));
const SubjectControlTypes = lazy(() => import("./pages/nomenclatures/SubjectControlTypes.tsx"));
const Subjects = lazy(() => import("./pages/nomenclatures/Subjects.tsx"));
const SubjectTypes = lazy(() => import("./pages/nomenclatures/SubjectTypes.tsx"));
const TeacherStatuses = lazy(() => import("./pages/nomenclatures/TeacherStatuses.tsx"));
const Universities = lazy(() => import("./pages/nomenclatures/Universities.tsx"));
const UniversityFields = lazy(() => import("./pages/nomenclatures/UniversityFields.tsx"));
const UniversitySpecialities = lazy(() => import("./pages/nomenclatures/UniversitySpecialities.tsx"));
const DormitoryApplications = lazy(() => import("./pages/students/reports/DormitoryApplications.tsx"));

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      {
        path: "dashboard",
        element: <Dashboard />,
      },
      {
        path: "students",
        element: <StudentsLayout />,
        children: [
          {
            path: "",
            element: <StudentsList />,
          },
          {
            path: ":studentId",
            element: <StudentProfile />,
          },
          {
            path: "create",
            element: <StudentProfile />,
          },
          {
            path: "enrollment",
            element: <Enrollment />,
          },
          {
            path: "paymentFiles",
            element: <PaymentFiles />,
          },
          {
            path: "reports/studentDormitoryApplications",
            element: <DormitoryApplications />,
          },
          {
            path: "reports/enrolledStudents",
            element: <EnrolledStudentsReport />,
          },
          {
            path: "reports/notTakenExams",
            element: <NotTakenExamsReport />,
          },
          {
            path: "reports/studentCountsGrouped",
            element: <StudentCountsGroupedReport />,
          },
          {
            path: "reports/groupAverageGrade",
            element: <GroupAverageGradeReport />,
          },
          {
            path: "reports/groupGrades",
            element: <GroupGradesReport />,
          },
        ],
      },
      {
        path: "plans",
        element: <PlansLayout />,
        children: [
          {
            path: "",
            element: <PlansList />,
          },
          {
            path: ":id",
            element: <PlanDetails />,
          },
          {
            path: "create",
            element: <PlanDetails />,
          },
        ],
      },
      {
        path: "protocols",
        element: <ProtocolsLayout />,
        children: [
          {
            path: "",
            element: <ProtocolsList />,
          },
          {
            path: ":id",
            element: <ProtocolDetails />,
          },
          {
            path: "create",
            element: <ProtocolCreate />,
          },
          {
            path: "createRecognitionProtocol",
            element: <ProtocolDetailsRecognition />,
          },
          {
            path: ":id/recognition",
            element: <ProtocolDetailsRecognition />,
          },
        ],
      },
      {
        path: "schedule",
        element: <ScheduleLayout />,
        children: [
          {
            path: "",
            element: <SchedulesList />,
          },
          {
            path: ":id",
            element: <ScheduleDetails />,
          },
          {
            path: "create",
            element: <ScheduleDetails />,
          },
        ],
      },
      {
        path: "nomenclatures",
        element: <NomenclaturesLayout />,
        children: [
          {
            path: "academicPositions",
            element: <AcademicPositions />,
          },
          {
            path: "academicRanks",
            element: <AcademicRanks />,
          },
          {
            path: "academicYears",
            element: <AcademicYears />,
          },
          {
            path: "admissionReasons",
            element: <AdmissionReasons />,
          },
          {
            path: "bankStatuses",
            element: <BankStatuses />,
          },
          {
            path: "buildings",
            element: <Buildings />,
          },
          {
            path: "cities",
            element: <Cities />,
          },
          {
            path: "countries",
            element: <Countries />,
          },
          {
            path: "degrees",
            element: <Degrees />,
          },
          {
            path: "departments",
            element: <Departments />,
          },
          {
            path: "faculties",
            element: <Faculties />,
          },
          {
            path: "fnPositions",
            element: <FnPositions />,
          },
          {
            path: "feeTypes",
            element: <FeeTypes />,
          },
          {
            path: "groups",
            element: <Groups />,
          },
          {
            path: "identificationTypes",
            element: <IdentificationTypes />,
          },
          {
            path: "masterDegreeTypes",
            element: <MasterDegreeTypes />,
          },
          {
            path: "municipalities",
            element: <Municipalities />,
          },
          {
            path: "planTypes",
            element: <PlanTypes />,
          },
          {
            path: "qualifications",
            element: <Qualifications />,
          },
          {
            path: "regions",
            element: <Regions />,
          },
          {
            path: "schoolProfiles",
            element: <SchoolProfiles />,
          },
          {
            path: "schools",
            element: <Schools />,
          },
          {
            path: "semesterTypes",
            element: <SemesterTypes />,
          },
          {
            path: "sessionTypes",
            element: <SessionTypes />,
          },
          {
            path: "specialities",
            element: <Specialities />,
          },
          {
            path: "specialityFees",
            element: <SpecialityFees />,
          },
          {
            path: "specialityForms",
            element: <SpecialityForms />,
          },
          {
            path: "specialityModes",
            element: <SpecialityModes />,
          },
          {
            path: "specialityTypes",
            element: <SpecialityTypes />,
          },
          {
            path: "studentStatuses",
            element: <StudentStatuses />,
          },
          {
            path: "subjectControlTypes",
            element: <SubjectControlTypes />,
          },
          {
            path: "subjects",
            element: <Subjects />,
          },
          {
            path: "subjectTypes",
            element: <SubjectTypes />,
          },
          {
            path: "teacherStatuses",
            element: <TeacherStatuses />,
          },
          {
            path: "universities",
            element: <Universities />,
          },
          {
            path: "universityFields",
            element: <UniversityFields />,
          },
          {
            path: "universitySpecialities",
            element: <UniversitySpecialities />,
          },
        ],
      },
      {
        path: "teachers",
        element: <TeachersLayout />,
        children: [
          {
            path: "",
            element: <TeachersList />,
          },
          {
            path: ":teacherId",
            element: <TeacherProfile />,
          },
          {
            path: "create",
            element: <TeacherProfile />,
          },
          {
            path: "report",
            element: <TeachersReport />,
          },
        ],
      },
      {
        path: "help",
        element: <Help />,
      },
    ],
  },
  {
    path: "/login",
    element: <Login />,
  },
]);
